<template>
  <div class="detail-score-panel" :class="{ 'is-readonly': readonly }">
    <div class="subject-nav">
      <button
        v-for="subject in subjects"
        :key="subject.key"
        type="button"
        class="subject-nav-item"
        :class="{ active: activeKey === subject.key }"
        @click="activeKey = subject.key"
      >
        <span>{{ subject.name }}</span>
        <strong>{{ subjectTotal(subject.key) }}分</strong>
      </button>
    </div>
    <div class="subject-content">
      <div class="subject-header">
        <div>
          <div class="subject-name">{{ activeSubject.name }}</div>
          <div class="subject-count">共 {{ activeSubject.count }} 题</div>
        </div>
        <div class="subject-total">合计 <strong>{{ subjectTotal(activeKey) }}</strong> 分</div>
      </div>
      <div class="question-grid">
        <div v-for="(column, columnIndex) in questionColumns" :key="columnIndex" class="question-column">
          <div v-for="index in column" :key="index" class="question-item">
            <span class="question-number">第 {{ index }} 题</span>
            <span v-if="readonly" class="readonly-score">{{ questionValue(activeKey, index - 1) }}</span>
            <a-radio-group
              v-else
              :value="questionValue(activeKey, index - 1)"
              button-style="solid"
              size="small"
              @change="changeScore(index - 1, $event.target.value)"
            >
              <a-radio-button
                v-for="option in scoreOptions(activeKey, index - 1)"
                :key="option"
                :value="option"
              >
                {{ option }}
              </a-radio-button>
            </a-radio-group>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const SUBJECTS_BY_EDITION = {
  1: [
    {key: 'rv', name: '指认图片', count: 31},
    {key: 'bd', name: '积木', count: 17},
    {key: 'pm', name: '图片记忆', count: 35},
    {key: 'in', name: '常识', count: 29},
    {key: 'oa', name: '拼图', count: 12},
    {key: 'zl', name: '动物家园', count: 20},
    {key: 'pn', name: '图片命名', count: 24},
  ],
  2: [
    {key: 'bd', name: '积木', count: 17},
    {key: 'in', name: '常识', count: 29},
    {key: 'mr', name: '矩阵推理', count: 26},
    {key: 'pm', name: '图片记忆', count: 35},
    {key: 'si', name: '类同', count: 23},
    {key: 'pc', name: '图画概念', count: 27},
    {key: 'zl', name: '动物家园', count: 20},
    {key: 'oa', name: '拼图', count: 12},
    {key: 'rv', name: '指认图片', count: 31},
    {key: 'pn', name: '图片命名', count: 24},
  ],
  3: [
    {key: 'bd', name: '积木', count: 17},
    {key: 'in', name: '常识', count: 29},
    {key: 'mr', name: '矩阵推理', count: 26},
    {key: 'pm', name: '图片记忆', count: 35},
    {key: 'si', name: '类同', count: 23},
    {key: 'oa', name: '拼图', count: 12},
    {key: 'rv', name: '指认图片', count: 31},
  ],
};

export default {
  name: 'WiscScoreDetail',
  props: {
    value: {
      type: Object,
      default: () => ({}),
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    edition: {
      type: [String, Number],
      default: '1',
    },
  },
  data() {
    return {
      activeKey: String(this.edition) === '1' ? 'rv' : 'bd',
    };
  },
  computed: {
    subjects() {
      return SUBJECTS_BY_EDITION[String(this.edition)] || SUBJECTS_BY_EDITION[1];
    },
    activeSubject() {
      return this.subjects.find((subject) => subject.key === this.activeKey) || this.subjects[0];
    },
    questionColumns() {
      const { count } = this.activeSubject;
      if (this.readonly) {
        return Array.from({ length: Math.ceil(count / 3) }, (_, row) =>
          [row * 3 + 1, row * 3 + 2, row * 3 + 3].filter((index) => index <= count)
        );
      }
      const half = Math.ceil(count / 2);
      return [
        Array.from({ length: half }, (_, index) => index + 1),
        Array.from({ length: count - half }, (_, index) => half + index + 1),
      ];
    },
  },
  watch: {
    value: {
      handler() {
        this.$emit('totals-change', this.calculateTotals(this.normalizedScores()));
      },
      immediate: true,
      deep: true,
    },
  },
  mounted() {
    if (!this.readonly) {
      this.$emit('input', this.normalizedScores());
    }
  },
  methods: {
    normalizedScores() {
      const normalized = {};
      this.subjects.forEach((subject) => {
        const values = Array.isArray(this.value && this.value[subject.key]) ? this.value[subject.key] : [];
        normalized[subject.key] = Array.from({length: subject.count}, (_, index) => {
          const value = Number(values[index]);
          return this.scoreOptions(subject.key, index).includes(value) ? value : 0;
        });
      });
      return normalized;
    },
    questionValue(subject, index) {
      return this.normalizedScores()[subject][index];
    },
    changeScore(index, value) {
      const scores = this.normalizedScores();
      this.$set(scores[this.activeKey], index, Number(value));
      this.$emit('input', scores);
      this.$emit('totals-change', this.calculateTotals(scores));
    },
    subjectTotal(subject) {
      return this.normalizedScores()[subject].reduce((total, value) => total + value, 0);
    },
    calculateTotals(scores) {
      return this.subjects.reduce((totals, subject) => {
        totals[`score_${subject.key}`] = scores[subject.key].reduce((total, value) => total + value, 0);
        return totals;
      }, {});
    },
    scoreOptions(subject, index) {
      if (subject === 'bd') {
        if (index < 4 || index === 8) return [0, 1, 2];
        return [0, 2];
      }
      if (subject === 'oa') {
        if (index < 4) return [0, 1];
        if ([4, 7, 9].includes(index)) return [0, 3];
        if ([8, 10, 11].includes(index)) return [0, 5];
        return [0, 2];
      }
      if (['2', '3'].includes(String(this.edition)) && subject === 'si') {
        return index < 6 ? [0, 1] : [0, 2];
      }
      return [0, 1];
    },
  },
};
</script>

<style scoped>
.detail-score-panel {
  display: grid;
  grid-template-columns: 180px minmax(0, 1fr);
  min-height: 360px;
  margin: 24px 0 34px;
  overflow: hidden;
  border: 1px solid #eadfea;
  border-radius: 6px;
  background: #fff;
}

.subject-nav {
  padding: 10px;
  border-right: 1px solid #eadfea;
  background: #faf7fa;
}

.subject-nav-item {
  display: flex;
  width: 100%;
  min-height: 42px;
  align-items: center;
  justify-content: space-between;
  padding: 0 10px;
  border: 0;
  border-radius: 4px;
  color: #555;
  background: transparent;
  cursor: pointer;
}

.subject-nav-item strong {
  color: #999;
  font-size: 12px;
}

.subject-nav-item.active {
  color: #832c75;
  background: #f1e7f0;
  font-weight: 600;
}

.subject-nav-item.active strong {
  color: #832c75;
}

.subject-content {
  min-width: 0;
  padding: 20px 22px 24px;
}

.subject-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 16px;
  border-bottom: 1px solid #eee;
}

.subject-name {
  color: #333;
  font-size: 16px;
  font-weight: 600;
}

.subject-count {
  margin-top: 3px;
  color: #999;
  font-size: 12px;
}

.subject-total {
  color: #666;
}

.subject-total strong {
  color: #832c75;
  font-size: 20px;
}

.question-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(260px, 1fr));
  gap: 10px 18px;
  padding-top: 18px;
}

.question-column {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.detail-score-panel.is-readonly .question-grid {
  grid-template-columns: repeat(3, minmax(150px, 1fr));
  gap: 10px 18px;
}

.detail-score-panel.is-readonly .question-column {
  display: contents;
}

.question-item {
  display: flex;
  min-height: 38px;
  align-items: center;
  justify-content: space-between;
  padding: 4px 8px 4px 12px;
  border-bottom: 1px solid #f1f1f1;
}

.question-number {
  flex: 0 0 62px;
  color: #666;
  font-size: 13px;
}

.readonly-score {
  width: 34px;
  height: 28px;
  border-radius: 4px;
  color: #832c75;
  background: #f1e7f0;
  font-weight: 600;
  line-height: 28px;
  text-align: center;
}

@media (max-width: 900px) {
  .detail-score-panel {
    grid-template-columns: 1fr;
  }

  .subject-nav {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    border-right: 0;
    border-bottom: 1px solid #eadfea;
  }

  .question-grid,
  .detail-score-panel.is-readonly .question-grid {
    grid-template-columns: 1fr;
  }
}
</style>
